<template>
  <div
    v-fluent="{ enabled: !isDisabled, effectSize: 3 }"
    :class="$style['action']"
    :style="`--__width: ${width}; --__height: ${height}`"
  >
    <FluentBackground
      :class="$style['action__background']"
      :gradient-size="200"
      :enabled="!isDisabled"
      light-color="rgba(255, 255, 255, 0.2)"
    >
      <component
        :is="tag"
        :class="{
          [$style['action__component']]: true,
          [$style['action__component--disabled']]: isDisabled,
          [$style['action__component--busy']]: isLoading,
        }"
        :disabled="isDisabled"
        :type="type"
        :data-testid="testId"
        :aria-busy="isLoading"
        :aria-disabled="isDisabled"
        :form="form"
        v-bind="$attrs"
        @click="$emit('click')"
      >
        <span :class="$style['action__inner']">
          <slot v-if="!isLoading" />
          <span
            v-else
            :class="$style['action__loading']"
          >
            <LoadingBubble />
          </span>
        </span>
      </component>
    </FluentBackground>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';

import { actionProps } from '../actionProps';
import { ActionState } from '../ActionState';
import FluentBackground from '~/components-daisy/fluent-effect/FluentBackground.vue';
import FluentBorder from '~/components-daisy/fluent-effect/FluentBorder.vue';
import { LoadingBubble } from '~/components-daisy/loading';
import { fluentDirective } from '~/components-daisy/fluent-effect/fluentDirective';

export default defineComponent({
  name: 'ActionSecondary',
  components: {
    FluentBackground,
    FluentBorder,
    LoadingBubble,
  },
  directives: {
    fluent: fluentDirective,
  },
  inheritAttrs: false,
  props: {
    ...actionProps,
    tag: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      required: false,
      default: '',
    },
    width: {
      type: String,
      required: false,
      default: 'clamp(250px, calc(10vw + 15rem), 325px)',
    },
    height: {
      type: String,
      required: false,
      default: '50px',
    },
    themeName: {
      type: String,
      required: false,
      default: 'actionPrimary',
    },
    form: {
      type: String,
      required: false,
      default: () => undefined,
    },
  },
  emits: ['click'],
  setup(props) {
    const isDisabled = computed(() => {
      switch (props.state) {
        case ActionState.Idle:
          return false;
        case ActionState.Busy:
          return true;
        case ActionState.Disabled:
          return true;
        default:
          return undefined;
      }
    });

    const isLoading = computed(() => {
      switch (props.state) {
        case ActionState.Idle:
          return false;
        case ActionState.Busy:
          return true;
        case ActionState.Disabled:
          return false;
        default:
          return undefined;
      }
    });

    return {
      isDisabled,
      isLoading,
    };
  },
});
</script>

<style lang="postcss" module>
.action {
  box-sizing: border-box;
  position: relative;
  isolation: isolate;
  width: var(--button-width, var(--__width));
  border-radius: var(--button-border-radius, var(--border-radius-600));
  box-shadow: var(--button-shadow, var(--shadow-200));
  min-width: 130px;

  @media (max-width: 400px) {
    --button-width: 100%;
    width: 100%;
  }

  &__background {
    position: relative;
    width: 100%;
    border-radius: inherit;
    background: var(--button-background, var(--color-mono-100));
  }

  &__component {
    position: relative;
    width: inherit;
    border-radius: inherit;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    flex-wrap: nowrap;
    background: transparent;
    cursor: pointer;
    overflow: hidden;
    outline: none;
    height: var(--button-height, var(--__height));
    padding: var(--button-padding, 10px 20px);
    color: var(--button-color, var(--color-primary-700));
    border: var(--button-border, none);
    font: var(--button-font, var(--font-400));
    font-size: 16px;
    -moz-osx-font-smoothing: antialiased;
    -webkit-font-smoothing: antialiased;
    touch-action: manipulation;

    &--disabled {
      cursor: not-allowed;
      opacity: 0.4;
    }

    &--busy {
      cursor: wait;
    }

    &:focus-visible {
      text-decoration: underline;
    }
  }

  &__inner {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    vertical-align: middle;
  }

  &__loading {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  @media only screen and (max-width: 499px) {
    align-self: center;
  }
}
</style>
